import React from 'react';
import useFlashOnChange from 'hooks/useFlashOnChange';
import { useScrollContext } from './ScrollableTableWrapper';

// On the cells, not the row: the row already plays its entrance (fadeUp), an
// element runs one `animation` value at a time, and sticky cells paint an
// opaque background over the row's.
const CELLS_FLASH_CLASS = '[&>td]:animate-flashUpdate';

/**
 * Composant réutilisable pour les lignes de table avec gestion de la sélection
 * @param {React.ReactNode} children - Contenu de la ligne (cellules <TableCell>)
 * @param {string|number} itemId - ID de l'item de la ligne
 * @param {string|number} selectedId - ID de l'item actuellement sélectionné
 * @param {function} onClick - Fonction appelée au clic sur la ligne
 * @param {string} className - Classes CSS additionnelles
 * @param {object} style - Style inline (ex. animationDelay de la cascade d'entrée)
 * @param {boolean} clickable - Si true, la ligne est cliquable avec cursor-pointer (défaut: true)
 * @param {*} flashSignature - Mutable fields of the item: the row lights up when they change after mount
 * @param {function} onAnimationEnd - Also called on the row's animationend (e.g. the exit of hooks/useLeavingItems)
 */
const TableRow = ({
  children,
  itemId,
  selectedId,
  onClick,
  className = '',
  clickable = true,
  id,
  style,
  flashSignature,
  onAnimationEnd: onRowAnimationEnd,
}) => {
  const { hasMoved = false, setHoveredRowId } = useScrollContext();
  const { flashClassName, onAnimationEnd: onFlashEnd } = useFlashOnChange(
    flashSignature,
    { identity: itemId, className: CELLS_FLASH_CLASS },
  );

  const handleAnimationEnd = (event) => {
    onFlashEnd(event);
    onRowAnimationEnd?.(event);
  };
  
  // Déterminer la classe de la ligne selon si elle est sélectionnée
  const rowClass =
    selectedId === itemId ? 'bg-yellow-50' : 'hover:bg-gray-50';

  const handleClick = (e) => {
    // Empêcher le clic si c'était un drag
    if (hasMoved) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (onClick) {
      onClick(itemId);
    }
  };

  const handleMouseEnter = () => {
    if (setHoveredRowId) {
      setHoveredRowId(itemId);
    }
  };

  const handleMouseLeave = () => {
    if (setHoveredRowId) {
      setHoveredRowId(null);
    }
  };

  return (
    <tr
      id={id}
      style={style}
      className={`group border-b border-gray-100 transition-colors duration-150 ${rowClass} ${clickable ? 'cursor-pointer' : ''} ${flashClassName} ${className}`}
      onClick={clickable ? handleClick : undefined}
      onAnimationEnd={handleAnimationEnd}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
    </tr>
  );
};

export default TableRow;

